/* ─────────────────────────────────────────────────────────────────────────────
   File: src/hooks/usePageTracking.js
   Ghi nhận lượt ra/vào web: mỗi lần mở app (đăng nhập, tải lại trang) tạo 1
   "phiên" (session) trong collection `user_sessions`; mỗi lần chuyển trang
   trong phiên đó ghi 1 dòng vào `page_views` (path, tên trang, thời gian vào,
   thời lượng ở lại). Dùng cho VisitAnalyticsPage (báo cáo tổng quan + cá nhân).
────────────────────────────────────────────────────────────────────────────── */
import { useEffect, useRef } from 'react';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export const TRACKING_COLLECTIONS = {
    SESSIONS: 'user_sessions',
    PAGE_VIEWS: 'page_views',
};

export default function usePageTracking({ userId, userName, role, pathname, pageLabel }) {
    const sessionIdRef  = useRef(null);
    const currentViewRef = useRef(null); // { docId, enteredAt: Date }

    // ── Mở 1 phiên truy cập mới khi có user (login / tải lại trang) ────────────
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;

        (async () => {
            try {
                const ref = await addDoc(collection(db, TRACKING_COLLECTIONS.SESSIONS), {
                    userId, userName: userName || null, role: role || null,
                    startedAt: serverTimestamp(), endedAt: null,
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                });
                if (!cancelled) sessionIdRef.current = ref.id;
            } catch (e) { console.error('usePageTracking: không tạo được session', e); }
        })();

        const closeSession = () => {
            if (!sessionIdRef.current) return;
            updateDoc(doc(db, TRACKING_COLLECTIONS.SESSIONS, sessionIdRef.current), { endedAt: serverTimestamp() }).catch(() => {});
        };
        const handleVisibility = () => { if (document.visibilityState === 'hidden') closeSession(); };
        window.addEventListener('beforeunload', closeSession);
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            cancelled = true;
            closeSession();
            window.removeEventListener('beforeunload', closeSession);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // ── Ghi 1 lượt xem trang mỗi khi route đổi ─────────────────────────────────
    useEffect(() => {
        if (!userId || !sessionIdRef.current || !pathname) return;
        let cancelled = false;

        // Đóng lượt xem trang TRƯỚC (tính thời lượng ở lại)
        const prev = currentViewRef.current;
        if (prev?.docId) {
            const durationSec = Math.max(0, Math.round((Date.now() - prev.enteredAt) / 1000));
            updateDoc(doc(db, TRACKING_COLLECTIONS.PAGE_VIEWS, prev.docId), {
                leftAt: serverTimestamp(), durationSec,
            }).catch(() => {});
        }

        (async () => {
            try {
                const ref = await addDoc(collection(db, TRACKING_COLLECTIONS.PAGE_VIEWS), {
                    sessionId: sessionIdRef.current, userId, userName: userName || null, role: role || null,
                    path: pathname, label: pageLabel || pathname,
                    enteredAt: serverTimestamp(), leftAt: null, durationSec: null,
                });
                if (!cancelled) currentViewRef.current = { docId: ref.id, enteredAt: Date.now() };
            } catch (e) { console.error('usePageTracking: không ghi được page view', e); }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, pathname]);
}